/**
 * Custom hook for managing appointments functionality
 */

import { useState, useEffect } from 'react';
import { Ticket, TicketAppointment } from '../../types/ticketTypes';
import { getDefaultAppointmentDate, getDefaultAppointmentTime } from '../utils/businessDayUtils';

export interface UseAppointmentsProps {
  selectedTicket: Ticket | null;
  onTicketUpdate?: (ticket: Ticket) => void;
  currentUser?: string;
}

export interface AppointmentsHandlers {
  appointmentDate: string;
  setAppointmentDate: (value: string) => void;
  appointmentTime: string;
  setAppointmentTime: (value: string) => void;
  appointmentTask: string;
  setAppointmentTask: (value: string) => void;
  appointmentOwner: string;
  setAppointmentOwner: (value: string) => void;
  editingAppointmentId: string | null;
  setEditingAppointmentId: (value: string | null) => void;
  handleAddAppointment: (ticket: Ticket) => void;
  handleEditAppointment: (ticket: Ticket, appointmentId: string) => void;
  handleUpdateAppointment: (ticket: Ticket, appointmentId: string) => void;
  handleToggleAppointmentStatus: (ticket: Ticket, appointmentId: string) => void;
  handleCancelAppointment: (ticket: Ticket, appointmentId: string) => void;
}

export const useAppointments = ({
  selectedTicket,
  onTicketUpdate,
  currentUser
}: UseAppointmentsProps): AppointmentsHandlers => {
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [appointmentTask, setAppointmentTask] = useState('');
  const [appointmentOwner, setAppointmentOwner] = useState('');
  const [editingAppointmentId, setEditingAppointmentId] = useState<string | null>(null);

  // Set default appointment date and time when a ticket is selected
  useEffect(() => {
    if (selectedTicket) {
      setAppointmentDate(getDefaultAppointmentDate());
      setAppointmentTime(getDefaultAppointmentTime());
    }
  }, [selectedTicket]);

  const handleAddAppointment = (ticket: Ticket) => {
    if (appointmentDate && appointmentTime && appointmentTask.trim()) {
      const newAppointment: TicketAppointment = {
        id: `apt-${Date.now()}`,
        date: appointmentDate,
        time: appointmentTime,
        task: appointmentTask.trim(),
        owner: appointmentOwner || ticket.assignedTo,
        createdBy: currentUser || 'Unknown User',
        createdAt: new Date().toISOString(),
        status: 'scheduled'
      };
      
      const updatedTicket = {
        ...ticket,
        appointments: [...ticket.appointments, newAppointment]
      };
      
      onTicketUpdate?.(updatedTicket);
      
      // Clear form
      setAppointmentDate('');
      setAppointmentTime('');
      setAppointmentTask('');
      setAppointmentOwner('');
    }
  };

  const handleToggleAppointmentStatus = (ticket: Ticket, appointmentId: string) => {
    const updatedAppointments = ticket.appointments.map(appointment => 
      appointment.id === appointmentId 
        ? { 
            ...appointment, 
            status: (appointment.status === 'scheduled' ? 'completed' : 'scheduled') as 'scheduled' | 'completed' | 'cancelled'
          }
        : appointment
    );
    
    const updatedTicket = {
      ...ticket,
      appointments: updatedAppointments
    };
    
    onTicketUpdate?.(updatedTicket);
  };

  const handleCancelAppointment = (ticket: Ticket, appointmentId: string) => {
    const updatedAppointments = ticket.appointments.map(appointment => 
      appointment.id === appointmentId 
        ? { ...appointment, status: 'cancelled' as const }
        : appointment
    );
    
    const updatedTicket = {
      ...ticket,
      appointments: updatedAppointments
    };
    
    onTicketUpdate?.(updatedTicket);
  };
  const handleEditAppointment = (ticket: Ticket, appointmentId: string) => {
    const appointment = ticket.appointments.find(apt => apt.id === appointmentId);
    if (appointment) {
      setAppointmentDate(appointment.date);
      setAppointmentTime(appointment.time);
      setAppointmentTask(appointment.task);
      setAppointmentOwner(appointment.owner);
      setEditingAppointmentId(appointmentId);
    }
  };

  const handleUpdateAppointment = (ticket: Ticket, appointmentId: string) => {
    if (appointmentDate && appointmentTime && appointmentTask.trim()) {
      const updatedAppointments = ticket.appointments.map(appointment => 
        appointment.id === appointmentId 
          ? { 
              ...appointment,
              date: appointmentDate,
              time: appointmentTime,
              task: appointmentTask.trim(),
              owner: appointmentOwner || appointment.owner
            }
          : appointment
      );
      
      const updatedTicket = {
        ...ticket,
        appointments: updatedAppointments
      };
      
      onTicketUpdate?.(updatedTicket);
      
      // Clear form and exit edit mode
      setAppointmentDate('');
      setAppointmentTime('');
      setAppointmentTask('');
      setAppointmentOwner('');
      setEditingAppointmentId(null);
    }
  };
  return {
    appointmentDate,
    setAppointmentDate,
    appointmentTime,
    setAppointmentTime,
    appointmentTask,
    setAppointmentTask,
    appointmentOwner,
    setAppointmentOwner,
    editingAppointmentId,
    setEditingAppointmentId,
    handleAddAppointment,
    handleEditAppointment,
    handleUpdateAppointment,
    handleToggleAppointmentStatus,
    handleCancelAppointment
  };
};